import React, { useState, useCallback } from 'react';
import { Notification, NotificationProps, NotificationVariant } from './Notification';

export interface NotificationItem {
  id: string;
  variant: NotificationVariant;
  message: string;
  title?: string;
  autoDismiss?: number;
}

export interface NotificationContainerProps {
  /** Position of the notification container */
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';
  /** Maximum number of notifications to display */
  maxNotifications?: number;
  /** Test ID for testing */
  testId?: string;
}

/**
 * NotificationContainer Component
 * 
 * A container component for managing and displaying multiple notifications.
 * Supports stacking, auto-dismiss, and positioning.
 * 
 * @example
 * ```tsx
 * const [notifications, setNotifications] = useState<NotificationItem[]>([]);
 * 
 * const addNotification = (notification: NotificationItem) => {
 *   setNotifications(prev => [...prev, notification]);
 * };
 * 
 * <NotificationContainer 
 *   notifications={notifications}
 *   onDismiss={(id) => setNotifications(prev => prev.filter(n => n.id !== id))}
 * />
 * ```
 */
export const NotificationContainer: React.FC<NotificationContainerProps & {
  notifications: NotificationItem[];
  onDismiss: (id: string) => void;
}> = ({
  notifications,
  onDismiss,
  position = 'top-right',
  maxNotifications = 5,
  testId,
}) => {
  // Position-specific classes
  const positionClasses: Record<string, string> = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  };

  // Container classes
  const containerClasses = [
    'fixed',
    'z-50',
    'flex',
    'flex-col',
    'gap-3',
    'pointer-events-none',
    positionClasses[position],
  ].join(' ');

  // Limit notifications to maxNotifications
  const visibleNotifications = (notifications || []).slice(-maxNotifications);

  return (
    <div
      className={containerClasses}
      data-testid={testId}
      aria-live="polite"
      aria-atomic="false"
    >
      {visibleNotifications.map((notification, index) => (
        <div
          key={notification.id}
          className="pointer-events-auto"
          style={{
            animationDelay: `${index * 50}ms`,
          }}
        >
          <Notification
            variant={notification.variant}
            message={notification.message}
            title={notification.title}
            autoDismiss={notification.autoDismiss}
            onClose={() => onDismiss(notification.id)}
            testId={testId ? `${testId}-notification-${notification.id}` : undefined}
          />
        </div>
      ))}
    </div>
  );
};

/**
 * Hook for managing notifications
 * 
 * @example
 * ```tsx
 * const { notifications, addNotification, removeNotification } = useNotifications();
 * 
 * addNotification({
 *   variant: 'success',
 *   message: 'Operation completed!',
 * });
 * ```
 */
export const useNotifications = () => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const addNotification = useCallback((
    notification: Omit<NotificationItem, 'id'>
  ) => {
    const id = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setNotifications(prev => [...prev, { ...notification, id }]);
    return id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    addNotification,
    removeNotification,
    clearNotifications,
  };
};
